/**
 * 🖼️ SupportersPage — PATCH-309 (2026-02)
 * ─────────────────────────────────────────────────────────
 * Public gallery of the four supporter tiers:
 *   • Sponsors        (org/corporate logos)
 *   • Donors          (individuals / gifts)
 *   • Supporters      (broader partners)
 *   • Residents & Staff
 *
 * Uses the existing site chrome (Header + Footer) so the page
 * feels at home inside the TEA brand. Media (images + short
 * videos) streams through /api/supporters/{id}/media — the
 * backend enforces the published + not-deleted gate on every
 * byte so unlisted drafts stay hidden.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Heart } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import useDocumentMeta from "../hooks/useDocumentMeta";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/site";
const MEDIA_BASE = (process.env.REACT_APP_BACKEND_URL || "");

export default function SupportersPage() {
  useDocumentMeta({
    title:       "Our Sponsors, Donors & Supporters | The Exchange Alliance",
            description: "The organisations, donors, residents, and staff who make it possible for The Exchange Alliance to serve the Last, the Least and the Lost.",
    canonical:   "https://theexchangealliance.com/supporters",
  });

  const [state,  setState]  = useState({ status: "loading" });
  const [active, setActive] = useState("all");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/supporters`);
        if (!cancelled) setState({ status: "loaded", data });
      } catch (e) {
        if (!cancelled) setState({
          status: "error",
          detail: e?.response?.data?.detail || "Failed to load supporters.",
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const categories = state.data?.categories || [];
  const grouped    = state.data?.grouped || {};
  const visibleGroups = useMemo(() => {
    if (active === "all") return categories;
    return categories.filter((c) => c.key === active);
  }, [active, categories]);

  const totalCount = state.data?.count ?? 0;

  return (
    <main data-testid="supporters-root" className="bg-[#FAFAF7] text-[#0A0612] font-sans min-h-screen">
      <Header />

      {/* ── Hero band ─────────────────────────────────────── */}
      <section className="pt-28 pb-10 px-6 lg:px-10 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <Heart className="h-4 w-4 text-[#C9A227]" />
          <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#C9A227]">
            Gratitude Wall
          </p>
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold tracking-tight text-[#0A0612] leading-[1.05] max-w-4xl">
          The people & organisations
          <br />
          <span className="text-[#C9A227]">behind our mission.</span>
        </h1>
        <p className="mt-4 max-w-2xl text-base text-[#6B6F76] leading-relaxed">
          Every sponsor, donor, resident, and staff member listed here has helped
          The Exchange Alliance serve the Last, the Least and the Lost — moving one
          more person from crisis to career.
        </p>
      </section>

      {/* ── Category filter chips ─────────────────────────── */}
      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-4">
        <div className="flex flex-wrap gap-2" data-testid="supporters-filter-bar">
          <FilterChip active={active === "all"} onClick={() => setActive("all")}
                      testid="supporters-filter-all">
            All · {totalCount}
          </FilterChip>
          {categories.map((c) => (
            <FilterChip
              key={c.key}
              active={active === c.key}
              onClick={() => setActive(c.key)}
              testid={`supporters-filter-${c.key}`}
            >
              {c.label} · {(grouped[c.key] || []).length}
            </FilterChip>
          ))}
        </div>
      </section>

      {/* ── Body ──────────────────────────────────────────── */}
      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-24">
        {state.status === "loading" && (
          <div className="py-24 text-center text-[#6B6F76]" data-testid="supporters-loading">
            <Loader2 className="h-6 w-6 animate-spin inline mr-2" />
            Loading gratitude wall…
          </div>
        )}
        {state.status === "error" && (
          <div className="py-24 text-center text-[#B91C1C]" data-testid="supporters-error">
            {state.detail}
          </div>
        )}
        {state.status === "loaded" && totalCount === 0 && (
          <div className="py-24 text-center" data-testid="supporters-empty">
            <div className="font-mono text-[11px] tracking-[0.22em] uppercase text-[#6B6F76] mb-2">
              No supporters yet
            </div>
            <p className="text-[#0A0612]/80">
              Our team is curating this wall. Check back soon.
            </p>
          </div>
        )}
        {state.status === "loaded" && visibleGroups.map((cat) => {
          const items = grouped[cat.key] || [];
          if (items.length === 0) return null;
          return (
            <div key={cat.key} className="mb-14" data-testid={`supporters-group-${cat.key}`}>
              <div className="flex items-center gap-3 mb-5">
                <div className="h-px flex-1 bg-[#E8E5DA]" />
                <h2 className="font-mono text-[11px] tracking-[0.28em] uppercase text-[#0A0612]">
                  {cat.label}
                </h2>
                <div className="h-px flex-1 bg-[#E8E5DA]" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {items.map((item) => (
                  <SupporterCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          );
        })}
      </section>

      <Footer />
    </main>
  );
}


/** Filter chip — pill shape, brand-accent when active. */
function FilterChip({ active, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-1.5 font-mono text-[10px] tracking-[0.22em] uppercase border transition-colors ${
        active
          ? "bg-[#0A0612] text-[#F4D88A] border-[#0A0612]"
          : "bg-white text-[#0A0612] border-[#E8E5DA] hover:border-[#0A0612]"
      }`}
    >
      {children}
    </button>
  );
}


/** Card — square media on top (image or auto-playing muted video),
 *  name underneath. Videos loop and stay muted so the wall never
 *  becomes noisy. */
function SupporterCard({ item }) {
  const src = `${MEDIA_BASE}${item.media_url}`;
  return (
    <article
      data-testid={`supporter-card-${item.id}`}
      className="group bg-white border border-[#E8E5DA] hover:border-[#C9A227] transition-colors overflow-hidden"
    >
      <div className="aspect-square bg-[#F5F5F0] overflow-hidden relative">
        {item.media_kind === "video" ? (
          <video
            src={src}
            muted
            loop
            autoPlay
            playsInline
            className="w-full h-full object-cover"
            data-testid={`supporter-media-video-${item.id}`}
          />
        ) : (
          <img
            src={src}
            alt={item.name}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            data-testid={`supporter-media-image-${item.id}`}
          />
        )}
        {item.featured && (
          <span className="absolute top-2 left-2 bg-[#C9A227] text-[#0A0612] font-mono text-[8px] tracking-[0.22em] uppercase px-2 py-0.5">
            Featured
          </span>
        )}
      </div>
      <div className="px-3 py-2.5">
        <p className="font-display text-[13px] font-semibold text-[#0A0612] leading-tight truncate"
           title={item.name}>
          {item.name}
        </p>
      </div>
    </article>
  );
}
